import { FC, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Box,
  FormControl,
  Alert,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from "@mui/material";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingButton from "@mui/lab/LoadingButton";
import { Autocomplete } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTagResource } from "../../../hooks/useTagResource";

interface CreateResourceGroupProps {
  arn: string;
  resourcesSelected: string[];
  regionsSelected: string[];
  changeTab: (index: number) => void;
}

const validationSchema = Yup.object().shape({
  createresourcegroup: Yup
    .string()
    .matches(/^[a-zA-Z0-9]{1,64}$/,"Please enter a valid name without any spaces")
    .required("Please enter a valid name without any spaces"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.string().required("Tag key is required"),
        value: Yup.string().required("Tag value is required")
      })
    )
    .min(1, "At least one tag is required"),
});

const CreateResourceGroup: FC<CreateResourceGroupProps> = ({
  arn,
  resourcesSelected,
  regionsSelected,
  changeTab
}) => {
  const { createGroupAndTagResources } = useTagResource();
  const [page, setPage] = useState(0);
  const [activeSpinner, setActiveSpinner] = useState(false); // spinner that displays while view is being created
  const [alerts, setAlerts] = useState<
    { severity: "error" | "success" | "info" | "warning"; msg: string | string[] }[]
  >([]);
  const predefinedTagValues: Record<string, string[]> = {
    "Organisation/Company": [],
    Department: [],
    Environment: ["Production", "Development", "Testing"],
    Owner: [],
    "Application Workload": [],
    CostCenter: [],
    Compliance: [
      "AWS Well Architected Framework",
      "Foundational Technical Review",
      "AWS Security on Ramp",
      "NIST 800-53 (Rev. 5) Low-Moderate-High",
      "NIST Cybersecurity Framework version 1.1",
      "AWS Foundational Security Best Practices v1.0.0",
      "CDR",
      "CIS AWS Foundations Benchmark v1.2.0",
      "CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2",
      "SOC 2",
      "PCI DSS V3.2.1",
      "ISO",
      "ISO-IEC 27001:2022 Annex A",
      "Secure Landing Zone Assessment",
      "Essential Eight",
      "NZISM",
      "AWS Generative AI Best Practices Framework v1",
      "Canadian Centre for Cyber Security - Medium",
      "FedRAMP Moderate Baseline",
      "Gramm-Leach-Bliley Act (GLBA)",
      "GxP 21 CFR Part 11",
      "HIPAA Final Omnibus Security Rule 2013",
      "HIPAA Security Rule 2003",
      "PCI DSS v4.0",
      "ACSC Information Security Manual",
      "CIS Controls v8 IG1",
      "CIS Controls v7.1 IG1",
      "NIST SP 800-171 Rev. 2",
      "GxP EU Annex 11",
      "CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.3.0, Level 1 and 2",
      "AWS Control Tower Guardrails",
    ],
    "Project/Purpose": [],
    "Compliance Framework": [],
    "Created by": [],
  };
  
  const knownTagKeys = Object.keys(predefinedTagValues);
  const formik = useFormik({
    initialValues: {
      createresourcegroup: "",
      tags: [{ key: "", value: "" }],
    },
    validationSchema,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setActiveSpinner(true);
      const success = await createGroupAndTagResources(
        arn,
        values.createresourcegroup,
        values.tags,
        regionsSelected,
        resourcesSelected,
        setAlerts
      );
      if (success) {
        changeTab(3);
      }
      setActiveSpinner(false);
    }
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <Grid
          container
          sx={{ pb: 2, pt: 2, pl: 2 }}
          spacing={2}
          alignItems="stretch"
        >
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="h6"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Provide a name for the tag Group and define the associated Tags
            </Typography>
          </Grid>
          <Grid item xs md={6}>
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="resource-explorer-create-resource-group"
                  placeholder="Tag Group Name"
                  label="Tag Group Name"
                  value={formik.values["createresourcegroup"]}
                  error={
                    formik.touched["createresourcegroup"] &&
                    Boolean(formik.errors["createresourcegroup"])
                  }
                  helperText={
                    formik.touched["createresourcegroup"] &&
                    formik.errors["createresourcegroup"]
                      ? String(formik.errors["createresourcegroup"])
                      : ""
                  }
                  onChange={(e) => formik.setFieldValue("createresourcegroup", e.target.value)}
                  onBlur={formik.handleBlur}
                />
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs md={6}></Grid>
          <Grid item xs md={12}>
            <Typography
              component={"span"}
              variant="h6"
              color="text.primary"
              sx={{ mb: 1.5 }}
              paragraph
            >
              Add Group Tags
            </Typography>
          </Grid>
          <FieldArray name="tags">
            {({ push, remove }) => (
              <>
                {formik.values.tags.map((tag, index) => {
                  const tagError = formik.errors.tags?.[index];
                  const valueError =
                    typeof tagError === "object" && tagError !== null && "value" in tagError
                      ? tagError.value
                      : undefined;
                  const keyError =
                    typeof tagError === "object" && tagError !== null && "key" in tagError
                      ? tagError.key
                      : undefined;
                  return(
                    <Grid container item spacing={1} key={index}>
                      <Grid item xs md={5} sx={{pt:0}}>
                      <Autocomplete
                        freeSolo
                        options={knownTagKeys}
                        value={tag.key || ""}
                        onChange={(_, value) =>
                          formik.setFieldValue(`tags[${index}].key`, value || "")
                        }
                        onInputChange={(_, value) =>
                          formik.setFieldValue(`tags[${index}].key`, value)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={`Tag Key ${index + 1}`}
                            error={formik.touched.tags?.[index]?.key && Boolean(keyError)}
                            helperText={formik.touched.tags?.[index]?.key && keyError}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <ExpandMoreIcon sx={{ ml: -4, pointerEvents: 'none' }} />
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      </Grid>
                      <Grid item xs md={5} sx={{pt:0}}>
                      <Autocomplete
                        freeSolo
                        options={
                          predefinedTagValues[tag.key] || [] // provide options only if key matches known
                        }
                        value={tag.value || ""}
                        onChange={(_, value) =>
                          formik.setFieldValue(`tags[${index}].value`, value || "")
                        }
                        onInputChange={(_, value) =>
                          formik.setFieldValue(`tags[${index}].value`, value)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={`Tag Value ${index + 1}`}
                            error={formik.touched.tags?.[index]?.value && Boolean(valueError)}
                            helperText={formik.touched.tags?.[index]?.value && valueError}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <ExpandMoreIcon sx={{ ml: -4, pointerEvents: 'none' }} />
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      </Grid>
                      <Grid item xs md={2}>
                        <IconButton
                          onClick={() => remove(index)}
                          disabled={formik.values.tags.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )
                })}
                <Grid item xs md={12} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={activeSpinner}
                    onClick={() => push({ key: "", value: "" })}
                  >
                    Add Tag
                  </Button>
                </Grid>
              </>
            )}
          </FieldArray>
          <Grid item xs md={10}></Grid>
          <Grid item xs md={2} sx={{mb:2}}>
            <LoadingButton
              id="wat-setup-btn"
              variant="contained"
              color="secondary"
              type="submit"
              loading={activeSpinner}
              sx={{
                width: 157,
                height: 40,
                bgcolor: "secondary.main",
                "&:hover": {
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                },
              }}
              loadingIndicator={
                <CircularProgress color={"secondary"} size={22} />
              }
            >
              Create & Tag
            </LoadingButton>
          </Grid>
          <Grid item xs md={12}>
          {alerts.map((alert, index) => (
            <Alert severity={alert.severity}>
                {Array.isArray(alert.msg) ? (
                  <>
                <Grid container flexDirection="row">
                  <Box component="div" sx={{ whiteSpace: "pre-wrap" }}>
                    <strong>{alert.msg[0]}</strong>
                    <Box
                      component="table"
                      sx={{
                        width: "100%",
                        mt: 1,
                        borderCollapse: "collapse",
                        "& td, & th": {
                          border: "1px solid #ccc",
                          padding: "4px 8px",
                          textAlign: "left",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow key={`Resource Errors-${index}`}>
                          <TableCell>Resource</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {alert.msg.slice(1)
                        .slice(page * 5, page * 5 + 5)
                        .map((row, i) => {
                          const [resource, ...rest] = row.split(" - ");
                          return (
                            <TableRow key={i}>
                              <TableCell>{resource.trim()}</TableCell>
                              <TableCell>{rest.join(" - ").trim()}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Box>
                  </Box>
                  </Grid>
                  <Grid
                    container
                    flexDirection="row"
                    justifyContent={"flex-end"}
                    sx={{ p: 0, pt: 3 }}
                  >
                    <Pagination
                      count={
                        alert.msg.slice(1).length
                          ? Math.ceil(alert.msg.slice(1).length / 5)
                          : 0
                      }
                      variant="outlined"
                      sx={{
                        color: "primary.main",
                        borderColor: "primary.main",
                      }}
                      shape="rounded"
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, value: number) => {
                        setPage(value - 1);
                      }}
                    />
                  </Grid>
                  </>
                ) : (
                  alert.msg
                )}
            </Alert>
          ))}
          </Grid>
        </Grid>
      </FormikProvider>
    </form>
  );
};

export default CreateResourceGroup;
